import moment from 'moment';
import { Reducer } from 'redux';

import {
  ACCOUNT_DETAILS_ONLY,
  DATE_FORMAT,
  LONG_DATE_FORMAT,
} from '../../shared/constants';
import {
  CustomerStore,
  CustomerStoreAction,
  CustomerStoreActionTypes,
  Owner,
} from './customerTypes';

const initialState: CustomerStore = {
  owners: null,
  careGivers: null,
  error: null,
  isLoading: false,
  createUserSuccessful: false,
  careTeam: null,
  careTeamDetails: null,
  facilityCustomers: null,
  didAddPrimaryContact: false,
  selectedTab: ACCOUNT_DETAILS_ONLY.ACCOUNT_DETAILS,
  careteamInvites: null,
  currentOwnerPin: null,
  deleteUserSuccessful: false,
  lastRefreshTime: null,
  isRefreshing: false,
  actionStatus: {
    success: false,
    error: null,
  },
  profilePicture: null,
  profilePictureLoading: false,
  profilePictureError: null,
  medicationImage: null,
  medicationImageLoading: false,
  customerIssues: [],
  customerIssuesLoading: false,
  customerSurveyResponses: [],
  customerSurveyLoading: false,
  customerWellnessResponses: [],
  customerWellnessLoading: false,
  customerSurveyHistory: null,
  customerSurveyHistoryLoading: false,
  customerSurveyHistoryError: null,
  customerSurveyHistoryRequestId: 0,
};

const customerReducer: Reducer<CustomerStore, CustomerStoreAction> = (
  state = initialState,
  action,
): CustomerStore => {
  switch (action.type) {
    case CustomerStoreActionTypes.SET_LAST_REFRESH:
      return {
        ...state,
        lastRefreshTime: new Date().toISOString(),
      };
    case CustomerStoreActionTypes.SET_IS_REFRESHING:
      return {
        ...state,
        isRefreshing: action.payload,
      };
    case CustomerStoreActionTypes.POPULATE_CUSTOMERS: {
      const { customerList } = action.payload;

      let owners: Owner[] = [];
      let careGivers: Owner[] = [];

      customerList.forEach((customer) => {
        if (customer.is_patient) owners.push(customer);
        else careGivers.push(customer);
      });
      owners.forEach((owner) => {
        let dob = moment(owner.date_of_birth);
        owner.date_of_birth = dob.isValid() ? dob.format(DATE_FORMAT) : '-';
        owner.name = `${owner.first_name} ${owner.last_name}`;
      });
      careGivers.forEach((careGiver) => {
        let dob = moment(careGiver.date_of_birth);
        careGiver.date_of_birth = dob.isValid() ? dob.format(DATE_FORMAT) : '-';
        careGiver.name = `${careGiver.first_name} ${careGiver.last_name}`;
      });
      let newState = {
        ...state,
        owners,
        careGivers,
        facilityCustomers: customerList,
      };
      return newState;
    }
    case CustomerStoreActionTypes.CUSTOMERS_API_ERR: {
      const { error } = action.payload;
      let newState = {
        ...state,
        error: error,
      };
      return newState;
    }
    case CustomerStoreActionTypes.CUSTOMERS_SET_LOAD_STATE: {
      const { isLoading } = action.payload;
      let newState = {
        ...state,
        didAddPrimaryContact: false,
        isLoading: isLoading,
      };
      return newState;
    }
    case CustomerStoreActionTypes.SUCCESS_CREATE_USER: {
      const { createUserSuccessful } = action.payload;
      let newState = {
        ...state,
        createUserSuccessful: createUserSuccessful,
      };
      return newState;
    }
    case CustomerStoreActionTypes.POPULATE_CUSTOMER: {
      const { customer } = action.payload;
      const { facilityCustomers } = state;
      let is_online = false;

      if (facilityCustomers) {
        const socketCustomer = facilityCustomers.find(
          ({ customer_id }) => customer_id === customer.customer_id,
        );
        if (socketCustomer) {
          is_online = !!socketCustomer.is_online;
        }
      }

      let dob = moment(customer.date_of_birth);
      let newFacilityCustomers = (state.facilityCustomers || []).filter(
        ({ customer_id }) => customer_id !== customer.customer_id,
      );
      let newState = {
        ...state,
        facilityCustomers: [
          ...newFacilityCustomers,
          {
            ...customer,
            long_format_dob: dob.isValid() ? dob.format(LONG_DATE_FORMAT) : '-',
            is_online,
            name: `${customer.first_name} ${customer.last_name}`,
          },
        ],
      };
      return newState;
    }
    case CustomerStoreActionTypes.POPULATE_CUSTOMER_CARE_TEAM: {
      const { careTeam } = action.payload;

      const filteredCareTeam = Object.entries(careTeam).reduce((acl, curr) => {
        const [customerId, careTeam] = curr;
        return {
          ...acl,
          [customerId]: careTeam.filter((a) => a.careteam_role_id),
        };
      }, {});
      let newState = {
        ...state,
        careTeam: { ...state.careTeam, ...filteredCareTeam },
      };
      return newState;
    }
    case CustomerStoreActionTypes.CLEAR_CUSTOMER_AND_CARE_TEAM: {
      let newState = {
        ...state,
        careTeam: initialState.careTeam,
        careTeamDetails: initialState.careTeamDetails,
        currentOwnerPin: initialState.currentOwnerPin,
      };
      return newState;
    }
    case CustomerStoreActionTypes.CREATE_PRIMARY_CONTACT_SUCCESS: {
      let newState = {
        ...state,
        isLoading: false,
        didAddPrimaryContact: true,
      };
      return newState;
    }
    case CustomerStoreActionTypes.POPULATE_CARE_TEAM_DETAILS: {
      const { careTeamDetails } = action.payload;
      const { facilityCustomers = [] } = state;
      let is_online = false;
      if (facilityCustomers) {
        const socketCustomer = (facilityCustomers || []).find(
          ({ customer_id }) => customer_id === careTeamDetails.customer_id,
        );
        if (socketCustomer) {
          is_online = !!socketCustomer.is_online;
        }
      }

      let newState = {
        ...state,
        careTeamDetails: {
          ...careTeamDetails,
          is_online,
        },
      };
      return newState;
    }
    case CustomerStoreActionTypes.CLEAR_CARE_TEAM_DETAILS:
      return {
        ...state,
        careTeamDetails: null,
      };

    case CustomerStoreActionTypes.POPULATE_CARETEAM_INVITES: {
      const { careteamInvites } = action.payload;
      let newState = {
        ...state,
        careteamInvites,
      };
      return newState;
    }
    case CustomerStoreActionTypes.CUSTOMERS_SIDE_BAR_TAB_SELECTION: {
      const { selectedTab } = action.payload;
      let newState = {
        ...state,
        selectedTab: selectedTab,
      };
      return newState;
    }
    case CustomerStoreActionTypes.SAVE_OWNER_PIN: {
      const { ownerPin } = action.payload;
      let newState = {
        ...state,
        currentOwnerPin: ownerPin,
      };
      return newState;
    }
    case CustomerStoreActionTypes.SUCCESS_DELETE_USER: {
      const { deleteUserSuccessful } = action.payload;
      return {
        ...state,
        deleteUserSuccessful,
      };
    }

    case CustomerStoreActionTypes.SET_ACTION_STATUS:
      return {
        ...state,
        actionStatus: {
          ...state.actionStatus,
          ...action.payload,
        },
      };
    case CustomerStoreActionTypes.CLEAR_ACTION_STATUS:
      return {
        ...state,
        actionStatus: {
          success: false,
          error: null,
        },
      };
    case CustomerStoreActionTypes.SET_PROFILE_PICTURE_LOADING:
      return {
        ...state,
        profilePictureLoading: action.payload,
      };
    case CustomerStoreActionTypes.SET_PROFILE_PICTURE:
      return {
        ...state,
        profilePicture: action.payload,
      };
    case CustomerStoreActionTypes.SET_PROFILE_PICTURE_ERROR:
      return {
        ...state,
        profilePictureError: action.payload,
      };

    case CustomerStoreActionTypes.SET_MEDICATION_IMAGE_LOADING:
      return {
        ...state,
        medicationImageLoading: action.payload,
      };
    case CustomerStoreActionTypes.SET_MEDICATION_IMAGE:
      return {
        ...state,
        medicationImage: action.payload,
        medicationImageLoading: false,
      };
    case CustomerStoreActionTypes.CLEAR_MEDICATION_IMAGE:
      return {
        ...state,
        medicationImage: null,
        medicationImageLoading: false,
      };
    case CustomerStoreActionTypes.FETCH_CUSTOMER_ISSUES_SUCCESS: {
      return {
        ...state,
        customerIssues: action.payload,
        customerIssuesLoading: false,
      };
    }
    case CustomerStoreActionTypes.SET_CUSTOMER_ISSUES_LOADING: {
      return {
        ...state,
        customerIssuesLoading: action.payload,
      };
    }
    case CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_SUCCESS: {
      return {
        ...state,
        customerSurveyResponses: action.payload,
        customerSurveyLoading: false,
      };
    }
    case CustomerStoreActionTypes.SET_CUSTOMER_SURVEY_LOADING: {
      return {
        ...state,
        customerSurveyLoading: action.payload,
      };
    }
    case CustomerStoreActionTypes.FETCH_CUSTOMER_WELLNESS_SUCCESS: {
      return {
        ...state,
        customerWellnessResponses: action.payload,
        customerWellnessLoading: false,
      };
    }
    case CustomerStoreActionTypes.SET_CUSTOMER_WELLNESS_LOADING: {
      return {
        ...state,
        customerWellnessLoading: action.payload,
      };
    }
    case CustomerStoreActionTypes.CUSTOMER_SURVEY_HISTORY_REQUEST: {
      return {
        ...state,
        customerSurveyHistoryLoading: true,
        customerSurveyHistoryError: null,
        customerSurveyHistoryRequestId: action.payload.requestId,
      };
    }
    case CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_SUCCESS: {
      // Drop responses superseded by a newer request (stale-thunk guard).
      if (action.payload.requestId !== state.customerSurveyHistoryRequestId) {
        return state;
      }
      const { requestId: _requestId, ...history } = action.payload;
      return {
        ...state,
        customerSurveyHistory: history,
        customerSurveyHistoryLoading: false,
        customerSurveyHistoryError: null,
      };
    }
    case CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_FAILURE: {
      if (action.payload.requestId !== state.customerSurveyHistoryRequestId) {
        return state;
      }
      return {
        ...state,
        customerSurveyHistoryLoading: false,
        customerSurveyHistoryError: action.payload.error,
      };
    }

    default:
      return state;
  }
};

export default customerReducer;
